import axios from 'axios';
import { getBrand, brandEndpoints } from '../config/brands';

// Determine the API base URL
const getApiBaseUrl = () => {
  // If REACT_APP_API_URL starts with '/', it's a relative URL (production)
  if (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.startsWith('/')) {
    return process.env.REACT_APP_API_URL;
  }
  // Otherwise use the full URL (development) or default to localhost
  return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
};

// Create axios instance with default config
const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('pizzainn_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    // Preserve the response structure that frontend expects
    const data = response.data;
    
    // If backend returns a structured response with success field, keep it
    if (data && typeof data === 'object' && 'success' in data) {
      return data;
    }
    
    // For non-structured responses, wrap them
    return {
      success: true,
      data: data
    };
  },
  (error) => {
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      data: error.response?.data
    });
    
    // Handle common errors
    const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'An error occurred';
    
    // Handle authentication errors
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('pizzainn_token');
      localStorage.removeItem('pizzainn_user');
      
      // Redirect to login if not already there
      if (!window.location.pathname.includes('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    
    // Handle server errors
    if (error.response?.status >= 500) {
      console.error('Server error:', error.response?.data);
    }
    
    // Create consistent error object with success: false structure
    const apiError = {
      success: false,
      message: errorMessage,
      status: error.response?.status,
      data: error.response?.data
    };
    
    return Promise.reject(apiError);
  }
);

// API endpoints
export const endpoints = {
  // Auth endpoints
  auth: {
    login: '/auth/login',
    register: '/auth/register',
    logout: '/auth/logout',
    me: '/auth/me',
    profile: '/auth/profile',
    changePassword: '/auth/change-password'
  },
  
  // Menu endpoints
  menu: {
    categories: '/menu/categories',
    items: '/menu/items',
    featured: '/menu/featured',
    search: '/menu/search'
  },
  
  // Mokaland Menu endpoints
  mokalandMenu: {
    categories: '/mokaland-menu/categories',
    items: '/mokaland-menu/items',
    featured: '/mokaland-menu/featured',
    search: '/mokaland-menu/search'
  },

  // Garden Menu endpoints
  qisparkiMenu: {
    categories: '/qisparki-menu/categories',
    items: '/qisparki-menu/items',
    featured: '/qisparki-menu/featured',
    search: '/qisparki-menu/search'
  },

  // Sədəf Menu endpoints (derived from the brand registry)
  sedefMenu: brandEndpoints(getBrand('sedef')),

  // ALL | Chef's Kitchen endpoints
  allMenu: {
    categories: '/all-menu/categories',
    items: '/all-menu/items',
    stats: '/all-menu/stats'
  },
  
  // Order endpoints
  orders: {
    create: '/orders',
    track: '/orders/track',
    history: '/orders/history',
    cancel: '/orders/cancel'
  },
  
  // Reservation endpoints
  reservations: {
    create: '/reservations',
    check: '/reservations/check-availability',
    cancel: '/reservations/cancel',
    update: '/reservations/update'
  },
  
  // Contact endpoints
  contact: {
    send: '/contact',
    subscribe: '/contact/subscribe'
  },
  
  // Gallery endpoints
  gallery: {
    images: '/gallery',
    upload: '/gallery/upload'
  },
  
  // Admin endpoints
  admin: {
    dashboard: '/admin/dashboard',
    orders: '/admin/orders',
    reservations: '/admin/reservations',
    menu: '/admin/menu',
    gallery: '/admin/gallery',
    contacts: '/admin/contacts',
    settings: '/admin/settings',
    analytics: '/admin/analytics'
  }
};

// Helper functions for common API operations
export const apiHelper = {
  // GET request
  get: async (url, params = {}) => {
    try {
      return await api.get(url, { params });
    } catch (error) {
      throw error;
    }
  },
  
  // POST request
  post: async (url, data = {}) => {
    try {
      return await api.post(url, data);
    } catch (error) {
      throw error;
    }
  },
  
  // PUT request
  put: async (url, data = {}) => {
    try {
      return await api.put(url, data);
    } catch (error) {
      throw error;
    }
  },
  
  // DELETE request
  delete: async (url) => {
    try {
      return await api.delete(url);
    } catch (error) {
      throw error;
    }
  },
  
  // PATCH request
  patch: async (url, data = {}) => {
    try {
      return await api.patch(url, data);
    } catch (error) {
      throw error;
    }
  },
  
  // Upload file
  upload: async (url, formData) => {
    try {
      return await api.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    } catch (error) {
      throw error;
    }
  }
};

// Utility functions
export const apiUtils = {
  // Format error message for user display
  formatError: (error) => {
    if (typeof error === 'string') return error;
    if (error.message) return error.message;
    if (error.data?.message) return error.data.message;
    return 'An unexpected error occurred';
  },
  
  // Check if request is loading
  isLoading: (requestState) => {
    return requestState === 'loading';
  },
  
  // Check if request failed
  isError: (requestState) => {
    return requestState === 'error';
  },
  
  // Check if request succeeded
  isSuccess: (requestState) => {
    return requestState === 'success';
  },
  
  // Build query string from object
  buildQueryString: (params) => {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        searchParams.append(key, params[key]);
      }
    });
    return searchParams.toString();
  },
  
  // Handle API response with standardized format
  handleResponse: (response) => {
    if (response.success) {
      return {
        success: true,
        data: response.data,
        message: response.message
      };
    } else {
      throw new Error(response.message || 'Request failed');
    }
  }
};

export default api;
